import { useCallback, useEffect } from 'react'
import SharedFilters from '../../../components/SharedFilters'
import AppPagination from '../../../components/AppPagination'
import { appToast } from '../../../components/AppToast'
import { useExportJob } from '../../../hooks/useExportJob'
import { useBirthdaysListScreen } from './hooks/useBirthdaysListScreen'

const SEARCH_FIELD_OPTIONS = [
  { value: 'Name', label: 'Name' },
  { value: 'Roll No.', label: 'Roll No.' },
  { value: 'Phone No.', label: 'Phone No.' },
  { value: 'Email', label: 'Email' },
  { value: 'ID No.', label: 'ID No.' },
]

function getInitials(firstName = '', lastName = '') {
  const f = (firstName || '').trim().charAt(0)
  const l = (lastName || '').trim().charAt(0)
  return `${f}${l}`.toUpperCase() || 'ST'
}

function formatDisplayDate(dob) {
  if (!dob) return '—'
  const d = new Date(dob)
  if (Number.isNaN(d.getTime())) return dob
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

function calculateAge(dob) {
  if (!dob) return null
  const birth = new Date(dob)
  if (Number.isNaN(birth.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const mDiff = now.getMonth() - birth.getMonth()
  if (mDiff < 0 || (mDiff === 0 && now.getDate() < birth.getDate())) {
    age--
  }
  return age >= 0 ? age : null
}

export default function BirthdaysListScreen() {
  const { state, actions } = useBirthdaysListScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  const toOptions = useCallback((arr) => {
    return (arr || []).map((item) =>
      typeof item === 'object' && item !== null
        ? item
        : { value: item, label: item }
    )
  }, [])

  const { triggerExport, isExporting } = useExportJob({ module: 'birthdays' })

  const handleExportCSV = () => {
    triggerExport({
      searchField: state.searchField,
      searchText: state.searchText,
      company: state.company,
      branch: state.branch,
      academicYear: state.academicYear,
      fromDate: state.fromDate,
      toDate: state.toDate,
      tab: state.tab,
    })
  }

  const fromEntry = state.totalCount === 0 ? 0 : (state.currentPage - 1) * state.pageSize + 1
  const toEntry = Math.min(state.currentPage * state.pageSize, state.totalCount)

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header (Shared ERP Theme) ─────────────────────────────────── */}
      <section className="flex flex-col gap-5 border-b border-outline-variant/10 pb-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Admission Module</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Birthdays
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Track student birthdays, schedule celebrations, and send personalized greetings.
          </p>
        </div>

        {/* ── Status Pill Segmented Tabs (Shared Layout) ──────────────────── */}
        <div className="flex items-center gap-2 rounded-xl bg-surface-container-high p-1">
          {['Active', 'Archive', 'All'].map((tabLabel) => {
            const tabKey = tabLabel.toLowerCase()
            const isActive = state.tab === tabKey
            return (
              <button
                key={tabKey}
                type="button"
                onClick={() => actions.setTab(tabKey)}
                className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-surface-container-lowest text-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {tabLabel}
              </button>
            )
          })}
        </div>
      </section>

      {/* ── KPI Summary Cards (Clean Theme Format matching user reference) ──── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Today */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">cake</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Today</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-on-surface tabular-nums">
              {state.summary.today}
            </p>
          </div>
        </div>

        {/* This Week */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <span className="material-symbols-outlined text-xl">event</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">This Week</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-emerald-700 tabular-nums">
              {state.summary.thisWeek}
            </p>
          </div>
        </div>

        {/* This Month */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">calendar_month</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">This Month</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-purple-700 tabular-nums">
              {state.summary.thisMonth}
            </p>
          </div>
        </div>

        {/* Total Listed */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">celebration</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Listed</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-amber-700 tabular-nums">
              {state.summary.total}
            </p>
          </div>
        </div>
      </div>

      {/* ── SharedFilters Component (Unified Project Pattern with Tools) ─────── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        showStatus={false}
        showSearchButtons={true}
        companyFilter={state.company}
        setCompanyFilter={actions.setCompany}
        companyOptions={toOptions(state.options.companyOptions)}
        branchFilter={state.branch}
        setBranchFilter={actions.setBranch}
        branchOptions={toOptions(state.options.branchOptions)}
        academicYearFilter={state.academicYear}
        setAcademicYearFilter={actions.setAcademicYear}
        academicYearOptions={toOptions(state.options.academicYearOptions)}
        periodFilter={state.datePeriod}
        setPeriodFilter={actions.handleDatePeriodChange}
        periodOptions={state.options.datePeriodOptions}
        periodLabel="Date: Period"
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        onSearch={actions.search}
        onReset={actions.resetFilters}
        resetLabel="Show All"
        visibleAccordionFilters={[
          'company',
          'academicYear',
          'branch',
          'period',
          'fromDate',
          'toDate',
        ]}
        tools={[
          { key: 'export', label: 'Export (CSV)', icon: 'download' },
          { key: 'print', label: 'Print List', icon: 'print' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export') handleExportCSV()
          else if (key === 'print') window.print()
        }}
      />

      {/* ── Data Table Section (Exact 6 Preserved Columns, No Right Action Buttons) ─ */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        {/* Table Title Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">cake</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              List of Student Birthdays
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-on-surface-variant">
              {state.totalCount} {state.totalCount === 1 ? 'Record' : 'Records'}
            </span>
          </div>
        </div>

        {/* Table Content: Exact 6 Columns Aligned Properly */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
                <th className="w-16 px-5 py-3.5 text-center font-semibold">Photo</th>
                <th className="px-5 py-3.5 font-semibold">Name</th>
                <th className="px-5 py-3.5 font-semibold">Mobile / Email</th>
                <th className="px-5 py-3.5 text-center font-semibold">Roll No</th>
                <th className="px-5 py-3.5 font-semibold">Birth Date</th>
                <th className="px-5 py-3.5 font-semibold">Branch</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-outline-variant/15 text-xs">
              {state.isLoading ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center text-sm font-medium text-on-surface-variant">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary/40 block mb-2">
                      progress_activity
                    </span>
                    Loading birthdays...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center">
                    <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/40">
                      cake
                    </span>
                    <p className="text-sm font-medium text-on-surface-variant">
                      No birthday records found matching current criteria.
                    </p>
                    <button
                      type="button"
                      onClick={actions.resetFilters}
                      className="mt-3 inline-flex items-center gap-1 rounded-xl bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
                    >
                      Show All
                    </button>
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const age = calculateAge(row.dob)
                  return (
                    <tr
                      key={row.id}
                      className="transition-colors hover:bg-surface-container-low/40"
                    >
                      {/* Col 1: Photo / Avatar */}
                      <td className="w-16 px-5 py-3.5 text-center align-middle">
                        <div className="flex justify-center">
                          {row.photoUrl ? (
                            <img
                              src={row.photoUrl}
                              alt={`${row.firstName} ${row.lastName}`}
                              className="h-10 w-10 rounded-full object-cover border border-outline-variant/30 shadow-xs"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-xs font-black text-primary border border-primary/20">
                              {getInitials(row.firstName, row.lastName)}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Col 2: Name */}
                      <td className="px-5 py-3.5 align-middle">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => actions.handleViewDetail(row.id)}
                            className="font-bold text-primary hover:underline text-left cursor-pointer text-sm"
                          >
                            {row.firstName} {row.lastName}
                          </button>
                          {row.studentCode ? (
                            <span className="text-[11px] font-medium text-on-surface-variant/70">
                              {row.studentCode}
                            </span>
                          ) : null}
                        </div>
                      </td>

                      {/* Col 3: Mobile / Email */}
                      <td className="px-5 py-3.5 align-middle">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold text-on-surface tabular-nums">
                            {row.mobile || '—'}
                          </span>
                          {row.email ? (
                            <span className="text-[11px] text-on-surface-variant/70">
                              {row.email}
                            </span>
                          ) : null}
                        </div>
                      </td>

                      {/* Col 4: Roll No */}
                      <td className="px-5 py-3.5 text-center align-middle">
                        <span className="inline-flex items-center justify-center rounded-md bg-surface-container-high px-2.5 py-0.5 text-xs font-bold text-on-surface-variant tabular-nums">
                          {row.rollNo || '—'}
                        </span>
                      </td>

                      {/* Col 5: Birth Date */}
                      <td className="px-5 py-3.5 align-middle">
                        <div className="flex flex-col">
                          <span className="font-bold text-on-surface text-xs tabular-nums">
                            {formatDisplayDate(row.dob)}
                          </span>
                          {age !== null && (
                            <span className="text-[11px] font-medium text-on-surface-variant/70">
                              {age} years old
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Col 6: Branch */}
                      <td className="px-5 py-3.5 align-middle">
                        <span className="font-medium text-on-surface">
                          {row.branch || '—'}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination Footer ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 border-t border-outline-variant/15 bg-surface-container-low/30 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs font-semibold text-on-surface-variant">
            Showing {fromEntry}–{toEntry} of {state.totalCount} entries
          </span>

          {state.totalPages > 1 && (
            <AppPagination
              page={state.currentPage}
              totalPages={state.totalPages}
              onPageChange={actions.setCurrentPage}
              onPrevious={() => actions.setCurrentPage(Math.max(1, state.currentPage - 1))}
              onNext={() => actions.setCurrentPage(Math.min(state.totalPages, state.currentPage + 1))}
              canPrevious={state.currentPage > 1}
              canNext={state.currentPage < state.totalPages}
            />
          )}
        </div>
      </section>
    </div>
  )
}
